<template>
	<SuiSelectList ref="$selectList" @navigate="scrollToIndex">
		<template #header>
			<UserAssignSelectHeader
				:title="title"
				:search-query="searchQuery"
				:enable-role-selection="enableRoleSelection"
				:has-individual-time-allocation="hasIndividualTimeAllocation"
				:users-list="usersList"
				:selected-users="selectedUsers"
				:selected-roles="selectedRoles"
				:pinned-user-positions="pinnedUserPositions"
				:has-custom-allocation="hasCustomAllocation"
				:confirmed-allocated-times="confirmedAllocatedTimes"
				:allocated-times="allocatedTimes"
				:task-users-availability="taskUsersAvailability"
				:active-role-filter-id="activeRoleFilterId"
				@close="onHeaderClose"
				@search="searchQuery = $event"
				@unselect="onUserClicked($event)"
				@update:has-custom-allocation="
					onUpdateHasCustomAllocation($event)
				"
				@update:allocated-times="
					$emit('update:allocated-times', $event)
				"
				@update:active-role-filter-id="onRoleFilterClicked"
			/>
		</template>
		<UserAssignSelectList
			:filtered-user-list="filteredUserList"
			:is-modify-enabled="isModifyEnabled"
			:is-all-users-selected="isAllUsersSelected"
			:selected-users="selectedUsers"
			:selected-roles="selectedRoles"
			:user-group-list="userGroupList"
			:selected-user-group="selectedUserGroup"
			:loading-users-and-groups="loadingUsersAndGroups"
			:closed-groups="closedGroups"
			:header-element="headerElement"
			:task-users-availability="taskUsersAvailability"
			:active-role-filter-id="activeRoleFilterId"
			:grouped-virtual-list="groupedVirtualList"
			:virtual-list-attrs="virtualListAttrs"
			:measure-element="measureElement"
			@select-all="onSelectAll"
			@select-user-group="onSelectUserGroup"
			@deselect-user-group="onDeselectUserGroup"
			@toggle-group-dropdown="onToggleGroupDropdown"
			@toggle-all-users-selected="toggleAllUsersSelected"
			@user-clicked="onUserClicked"
			@role-clicked="onRoleClicked"
		/>
		<template v-if="isModifyEnabled" #footer>
			<UserAssignSelectFooter
				:is-notify-enabled="isNotifyEnabled"
				:should-notify="shouldNotify"
				:active-role-filter-id="activeRoleFilterId"
				@update:should-notify="shouldNotify = $event"
				@assign-users="onAssignUsers"
				@remove-role="onRemoveRole"
			/>
		</template>
	</SuiSelectList>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import {
	SuiSelectList,
	type SuiSelectListGroupType,
	type SuiSelectListItemsAndGroupsValueType,
} from '@developers/scoro-ui';
import {
	type User,
	UserType,
	UserStatus,
	type Task,
	type UserAssignSelectUserFragment,
	type Role,
	type UserAssignSelectAvailabilityQuery,
	type UserAssignSelectUsersAndGroupsQuery,
	useUserAssignSelectAvailabilityLazyQuery,
	UserAssignSelectAvailabilityDocument,
} from '@/generated/graphql';
import uniq from 'lodash/uniq';
import {
	UserAssignGroups,
	type PinnedUserPositions,
	type UserAssignGroup,
	type UserAssignSelectRole,
	type UserAssignSelectAssigneesPayload,
} from '../../types';
import {
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from '@/features/Task/components/TaskUsers/types';
import { UserAssignSelectHeader } from './Header';
import { UserAssignSelectList } from './List';
import UserAssignSelectFooter from './UserAssignSelectFooter.vue';
import { getInitials } from '../../utils';
import { secondsToTimeAllocation } from '@/utils/time';
import {
	useUserAssignRoleFilter,
	useUserAssignVirtualList,
} from '../../composables';

type UserGroupsListData = UserAssignSelectUsersAndGroupsQuery['UserGroupsList'];
type RolesListData = NonNullable<
	UserAssignSelectUsersAndGroupsQuery['RolesList']
>;

export interface UserAssignSelectDropdownProps {
	title?: string;
	isModifyEnabled: boolean;
	isOwnerExcludedFromSelection: boolean;
	owner: UserAssignSelectUserFragment | undefined | null;
	userIds: Array<User['id']>;
	assignedRoles: UserAssignSelectRole[];
	enableRoleSelection: boolean;
	isGroupedByRoles?: boolean;
	subGroupTitles: string[];
	filterByRoleId?: Role['id'];
	taskId?: Task['id'];
	isNotifyEnabled?: boolean;
	hasIndividualTimeAllocation?: boolean;
	allocatedTimes?: TaskAssigneeAllocatedDuration;
	confirmedAllocatedTimes?: TaskAssigneeAllocatedDuration;
	hasCustomAllocation: boolean;
	plannedDuration?: Task['durationPlanned'];
	usersList: UserAssignSelectUserFragment[];
	projectMemberIds: Array<User['id']>;
	userGroupsListData: UserGroupsListData;
	rolesListData: RolesListData;
	loadingUsersAndGroups: boolean;
	isOpen: boolean;
}

const props = withDefaults(defineProps<UserAssignSelectDropdownProps>(), {
	title: undefined,
	isGroupedByRoles: false,
	filterByRoleId: undefined,
	taskId: undefined,
	isNotifyEnabled: false,
	hasIndividualTimeAllocation: false,
	allocatedTimes: undefined,
	confirmedAllocatedTimes: undefined,
	plannedDuration: undefined,
});

const emit = defineEmits<{
	(e: 'update:assignees', payload: UserAssignSelectAssigneesPayload): void;
	(e: 'update:allocated-times', payload: TaskAssigneeInputDuration): void;
	(e: 'update:has-custom-allocation', value: boolean): void;
	(e: 'role-replaced-with-user'): void;
	(e: 'close'): void;
}>();

const $selectList = ref<InstanceType<typeof SuiSelectList> | null>(null);

// Task-user availability query. Lives in the dropdown so it's torn down when
// the popper unmounts. Gates on props.taskId so non-task consumers
// (projects/events) don't fire it.
const {
	load: loadTaskUsersAvailability,
	refetch: refetchTaskUsersAvailability,
	result: taskUsersAvailabilityResult,
} = useUserAssignSelectAvailabilityLazyQuery();

const taskUsersAvailability = computed<
	UserAssignSelectAvailabilityQuery['TaskUsersAvailability']
>(() => taskUsersAvailabilityResult.value?.TaskUsersAvailability ?? []);

const loadOrRefetchAvailability = () => {
	if (!props.taskId) {
		return;
	}
	const variables = {
		input: {
			taskId: props.taskId,
		},
	};
	if (
		!loadTaskUsersAvailability(
			UserAssignSelectAvailabilityDocument,
			variables
		)
	) {
		refetchTaskUsersAvailability(variables);
	}
};

const searchQuery = ref('');
const selectedUsers = ref<Array<User['id']>>([...props.userIds]);
const selectedRoles = ref<UserAssignSelectRole[]>([...props.assignedRoles]);
const activeRoleFilterId = ref<UserAssignSelectRole['id'] | null>(null);
const closedGroups = ref<UserAssignGroup['id'][]>([]);
const closedGroupsBeforeFilter = ref<UserAssignGroup['id'][]>([]);
const pinnedUserPositions = ref<PinnedUserPositions>(new Map());
const shouldNotify = ref(false);

const headerElement = () => document.querySelector('.sui-select-list__header');
const listContentElement = () =>
	document.querySelector('.sui-select-list__content');

const normalizedSearchQuery = computed(() =>
	searchQuery.value.toLowerCase().trim()
);

const isSearchMatch = (text: string | undefined | null): boolean => {
	if (!text) {
		return false;
	}

	return text.toLowerCase().trim().includes(normalizedSearchQuery.value);
};

const filteredUserList = computed(() => {
	if (!props.usersList) {
		return [];
	}

	let userListFiltered = props.usersList;

	if (props.isOwnerExcludedFromSelection) {
		userListFiltered = props.usersList.filter(
			(user) => user.id !== props.owner?.id
		);
	}

	return userListFiltered.filter((user) => {
		const nameMatches = isSearchMatch(user.name);

		if (props.enableRoleSelection) {
			const roleNameMatches = isSearchMatch(user.role?.name);
			return nameMatches || roleNameMatches;
		}

		return nameMatches;
	});
});

// inactive users stay in filteredUserList so assigned avatars still render
const activeFilteredUserList = computed(() =>
	filteredUserList.value.filter((user) => user.status !== UserStatus.Inactive)
);

const projectMembersList = computed(() => {
	if (!filteredUserList.value) {
		return [];
	}

	return filteredUserList.value.filter((user) =>
		props.projectMemberIds.includes(user.id)
	);
});

const filterUnassignedUsers = (isCupoUser: boolean) => {
	if (!activeFilteredUserList.value) {
		return [];
	}

	return activeFilteredUserList.value.filter(
		(user) =>
			!props.projectMemberIds.includes(user.id) &&
			(isCupoUser
				? user.type === UserType.Cupo
				: user.type !== UserType.Cupo)
	);
};

const unAssignedUserList = computed(() => filterUnassignedUsers(false));
const unAssignedCupoUserList = computed(() => filterUnassignedUsers(true));

const isAllProjectMembersSelected = computed(() => {
	if (projectMembersList.value.length === 0) {
		return false;
	}
	const notSelectedUsers = projectMembersList.value.filter(
		(member) => !selectedUsers.value.includes(member.id)
	);

	return notSelectedUsers.length === 0;
});

const isAllNotAssignedUsersSelected = computed(() => {
	if (unAssignedUserList.value.length === 0) {
		return false;
	}
	const notSelectedUsers = unAssignedUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedUsers.length === 0;
});

const isAllNotAssignedCupoUsersSelected = computed(() => {
	if (unAssignedCupoUserList.value.length === 0) {
		return false;
	}
	const notSelectedCupoUsers = unAssignedCupoUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedCupoUsers.length === 0;
});

const isAllUsersSelected = computed(() => {
	if (activeFilteredUserList.value.length === 0) {
		return false;
	}
	const notSelectedUsers = activeFilteredUserList.value.filter(
		(user) => !selectedUsers.value.includes(user.id)
	);

	return notSelectedUsers.length === 0;
});

const buildRoleGroupsForAssignRolesToTasks = (
	groups: UserAssignGroup[]
): UserAssignGroup[] => {
	const roleGroups = [...props.rolesListData]
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((role) => {
			let usersInRole = unAssignedUserList.value.filter(
				(user) => user.role?.id === role.id
			);

			if (activeRoleFilterId.value) {
				usersInRole = usersInRole.filter(
					(user) => !selectedUsers.value.includes(user.id)
				);
			}

			const isRoleMatchesSearch = isSearchMatch(role.name);

			const shouldShowRoleItem =
				!activeRoleFilterId.value &&
				(isRoleMatchesSearch || usersInRole.length > 0);

			const roleWithInitials = {
				...role,
				initials: role.initials || getInitials(role.name),
			};

			return {
				id: role.name,
				name: role.name,
				role: roleWithInitials,
				isRoleSelectable:
					props.enableRoleSelection &&
					shouldShowRoleItem &&
					isRoleMatchesSearch,
				userList: usersInRole,
				isAllUsersSelected: usersInRole.every((user) =>
					selectedUsers.value.includes(user.id)
				),
			};
		});

	if (activeRoleFilterId.value || normalizedSearchQuery.value !== '') {
		groups[0] = {
			...groups[0],
			userList: groups[0].userList.filter((user) => {
				const isVisibleByName =
					normalizedSearchQuery.value === '' ||
					isSearchMatch(user.name);
				const isAvailableForSelection =
					!activeRoleFilterId.value ||
					!selectedUsers.value.includes(user.id);

				return isVisibleByName && isAvailableForSelection;
			}),
		};
	}

	return [...groups, ...roleGroups];
};

const userGroupListToDisplay = computed(() => {
	let groups: UserAssignGroup[] = [
		{
			id: UserAssignGroups.ProjectMembers,
			name: props.subGroupTitles[0],
			userList: projectMembersList.value,
			isAllUsersSelected: isAllProjectMembersSelected.value,
			role: null,
			isRoleSelectable: false,
		},
	];

	if (props.enableRoleSelection) {
		groups = buildRoleGroupsForAssignRolesToTasks(groups);
	} else {
		if (props.isGroupedByRoles) {
			const groupedByRoles: Record<
				Role['name'],
				UserAssignSelectUserFragment[]
			> = unAssignedUserList.value.reduce(
				(acc, user) => {
					const roleId = user.role?.name || props.subGroupTitles[1];
					if (!acc[roleId]) {
						acc[roleId] = [];
					}
					acc[roleId].push(user);
					return acc;
				},
				{} as Record<Role['name'], UserAssignSelectUserFragment[]>
			);

			groups = [
				...groups,
				...Object.entries(groupedByRoles).map(([roleName, users]) => ({
					id: roleName,
					name: roleName,
					role: null,
					isRoleSelectable: false,
					userList: users,
					isAllUsersSelected: users.every((user) =>
						selectedUsers.value.includes(user.id)
					),
				})),
			];
		} else if (props.isModifyEnabled) {
			groups = [
				...groups,
				{
					id: UserAssignGroups.Other,
					name: props.subGroupTitles[1],
					userList: unAssignedUserList.value,
					isAllUsersSelected: isAllNotAssignedUsersSelected.value,
					role: null,
					isRoleSelectable: false,
				},
				{
					id: UserAssignGroups.Cupo,
					name: props.subGroupTitles[2],
					userList: unAssignedCupoUserList.value,
					isAllUsersSelected: isAllNotAssignedCupoUsersSelected.value,
					role: null,
					isRoleSelectable: false,
				},
			];
		}
	}

	return groups.filter(
		(group) => group.isRoleSelectable || group.userList.length > 0
	);
});

const { scrollToGroup } = useUserAssignRoleFilter({
	userGroupListToDisplay,
	activeRoleFilterId,
});

const scrollElement = computed(
	() => $selectList.value?.$refs?.selectListContent as HTMLElement | null
);

const { groupedVirtualList, virtualListAttrs, measureElement, scrollToIndex } =
	useUserAssignVirtualList({
		userGroupListToDisplay,
		closedGroups,
		scrollElement,
	});

const updateGroupsForRoleFilter = () => {
	if (!activeRoleFilterId.value) {
		closedGroups.value = [...closedGroupsBeforeFilter.value];
		closedGroupsBeforeFilter.value = [];
	} else {
		if (closedGroupsBeforeFilter.value.length === 0) {
			closedGroupsBeforeFilter.value = [...closedGroups.value];
		}
		closedGroups.value = userGroupListToDisplay.value
			.filter(
				(group) =>
					group.role?.id !== activeRoleFilterId.value &&
					group.id !== UserAssignGroups.ProjectMembers
			)
			.map((group) => group.id);
	}
};

const onRoleFilterClicked = (roleId: UserAssignSelectRole['id']) => {
	if (activeRoleFilterId.value === roleId) {
		activeRoleFilterId.value = null;
	} else {
		activeRoleFilterId.value = roleId;
		scrollToGroup(listContentElement(), headerElement());
	}

	updateGroupsForRoleFilter();
};

const unSelectGroup = (
	group: Array<User['id']>,
	selected: Array<User['id']>
) => {
	selectedUsers.value = selected.filter(
		(userId) => !group.some((id) => id === userId)
	);
};

const onSelectAll = (isSelectAll: boolean) => {
	if (isSelectAll) {
		// selecting all must not unassign already-assigned inactive users
		selectedUsers.value = uniq([
			...selectedUsers.value,
			...activeFilteredUserList.value.map((user) => user.id),
		]);
	} else {
		selectedUsers.value = [];
	}
};

const toggleAllUsersSelected = (
	group: UserAssignGroup,
	isSelectAll: boolean
) => {
	const selected = [...selectedUsers.value];

	if (!isSelectAll) {
		unSelectGroup(
			group.userList.map((user) => user.id),
			selected
		);
	} else {
		group.userList.forEach((user) => selected.push(user.id));
		selectedUsers.value = uniq(selected);
	}
};

const copyAllocatedTimes = (): TaskAssigneeInputDuration => {
	const times: TaskAssigneeInputDuration = {
		users: {},
		roles: {},
	};

	if (!props.allocatedTimes) {
		return times;
	}

	props.allocatedTimes.users.forEach((time, userId) => {
		times.users[userId] = secondsToTimeAllocation(time);
	});

	props.allocatedTimes.roles.forEach((time, roleId) => {
		times.roles[roleId] = secondsToTimeAllocation(time);
	});

	return times;
};

const replaceActiveRoleWithUser = (
	userId: UserAssignSelectUserFragment['id']
) => {
	if (!activeRoleFilterId.value) {
		return;
	}

	const roleTime = props.allocatedTimes?.roles.get(activeRoleFilterId.value);

	if (roleTime) {
		const updatedTimes = copyAllocatedTimes();

		updatedTimes.users[userId] = secondsToTimeAllocation(roleTime);

		emit('update:allocated-times', updatedTimes);
	}

	const replacedRole = selectedRoles.value.find(
		(role) => role.id === activeRoleFilterId.value
	);

	if (replacedRole) {
		pinnedUserPositions.value.set(userId, replacedRole);
	}

	selectedRoles.value = selectedRoles.value.filter(
		(role) => role.id !== activeRoleFilterId.value
	);
	activeRoleFilterId.value = null;
	closedGroups.value = [...closedGroupsBeforeFilter.value];
	emit('role-replaced-with-user');
};

const onUserClicked = (id: UserAssignSelectUserFragment['id']) => {
	if (selectedUsers.value.includes(id)) {
		selectedUsers.value = selectedUsers.value.filter(
			(userId) => userId !== id
		);
		pinnedUserPositions.value.delete(id);
	} else {
		selectedUsers.value = [...selectedUsers.value, id];
		replaceActiveRoleWithUser(id);
	}
};

const onRoleClicked = (role: UserAssignSelectRole) => {
	if (selectedRoles.value.some((r) => r.id === role.id)) {
		selectedRoles.value = selectedRoles.value.filter(
			(r) => r.id !== role.id
		);
	} else {
		selectedRoles.value = [...selectedRoles.value, role];
	}
};

const onHeaderClose = () => {
	if (activeRoleFilterId.value) {
		activeRoleFilterId.value = null;
		updateGroupsForRoleFilter();
	} else {
		emit('close');
	}
};

const onToggleGroupDropdown = (target: HTMLElement, group: UserAssignGroup) => {
	if (target.nodeName !== 'LABEL' && target.nodeName !== 'INPUT') {
		if (closedGroups.value.includes(group.id)) {
			closedGroups.value = closedGroups.value.filter(
				(id) => id !== group.id
			);
		} else {
			closedGroups.value.push(group.id);
		}
	}
};

const updateAssignees = () => {
	const users =
		props.usersList.filter((user) =>
			selectedUsers.value.includes(user.id)
		) || [];
	const hasCupoUsers =
		props.usersList.filter((user) => user.type === UserType.Cupo).length >
		0;

	emit('update:assignees', {
		users,
		hasCupoUsers,
		shouldNotify: shouldNotify.value,
		roles: selectedRoles.value,
	});
};

const onAssignUsers = () => {
	updateAssignees();
	emit('close');
};

const onRemoveRole = () => {
	selectedRoles.value = selectedRoles.value.filter(
		(role) => role.id !== activeRoleFilterId.value
	);
	activeRoleFilterId.value = null;
	updateGroupsForRoleFilter();
};

const onUpdateHasCustomAllocation = (hasCustomAllocation: boolean) => {
	if (
		hasCustomAllocation &&
		props.plannedDuration &&
		(selectedUsers.value.length > 0 || selectedRoles.value.length > 0)
	) {
		const totalCount =
			selectedUsers.value.length + selectedRoles.value.length;
		const equalTime = Math.floor(props.plannedDuration / totalCount);

		const timeInputs: TaskAssigneeInputDuration = {
			users: {},
			roles: {},
		};

		selectedUsers.value.forEach((userId) => {
			timeInputs.users[userId] = secondsToTimeAllocation(equalTime);
		});

		selectedRoles.value.forEach((role) => {
			timeInputs.roles[role.id] = secondsToTimeAllocation(equalTime);
		});

		emit('update:allocated-times', timeInputs);
	}

	emit('update:has-custom-allocation', hasCustomAllocation);
};

const userGroupList = computed<SuiSelectListGroupType[]>(() => {
	if (!props.userGroupsListData) {
		return [];
	}

	return props.userGroupsListData
		.map(({ id, name, users }) => ({
			id,
			name,
			items: users
				.filter(({ status }) => status !== UserStatus.Inactive)
				.map(({ id }) => id),
		}))
		.filter((group) => group.items.length > 0)
		.sort((a, b) => a.name.localeCompare(b.name));
});

const selectedUserGroup = computed(
	(): SuiSelectListItemsAndGroupsValueType => ({
		itemIds: selectedUsers.value,
		groupIds: [],
	})
);

const onSelectUserGroup = (groupId: SuiSelectListGroupType['id']) => {
	const selectedGroup = userGroupList.value.find(
		(group) => group.id === groupId
	);

	if (selectedGroup) {
		selectedUsers.value = uniq(
			selectedUsers.value.concat(selectedGroup.items || [])
		);
	}
};

const onDeselectUserGroup = (groupId: SuiSelectListGroupType['id']) => {
	const selectedGroup = userGroupList.value.find(
		(group) => group.id === groupId
	);

	if (selectedGroup) {
		selectedUsers.value = selectedUsers.value.filter(
			(id) => !selectedGroup.items?.includes(id)
		);
	}
};

const resetMinHeight = async () => {
	await nextTick();

	const selectListContent = $selectList.value?.$refs?.selectListContent;

	if (selectListContent instanceof HTMLDivElement) {
		selectListContent.style.minHeight = `min(${selectListContent.offsetHeight}px, var(--sui-select-list-content-max-height))`;
	}
};

const focusSearchInput = () => {
	// Delay needed - SuiSelectListSearch autofocus fires before popover is fully rendered. nextTick would still be too early.
	setTimeout(() => {
		const input = $selectList.value?.$el?.querySelector(
			'.sui-select-list-search input'
		) as HTMLInputElement | null;
		input?.focus();
	}, 50);
};

const resetState = () => {
	searchQuery.value = '';
	activeRoleFilterId.value = null;
	closedGroups.value = [];
	selectedRoles.value = [...props.assignedRoles];
	selectedUsers.value = [...props.userIds];
	pinnedUserPositions.value = new Map();

	nextTick(() => {
		focusSearchInput();

		const element = listContentElement();
		if (element) {
			element.scrollTop = 0;
		}
	});
};

onMounted(() => {
	resetMinHeight();
});

watch(
	() => props.isOpen,
	(isOpen) => {
		if (isOpen) {
			resetState();
			loadOrRefetchAvailability();
		}
	},
	{ immediate: true }
);

watch(
	() => props.userGroupsListData,
	() => {
		resetMinHeight();
	}
);

watch(
	[selectedUsers, selectedRoles],
	(
		[newSelectedUsers, newSelectedRoles],
		[oldSelectedUsers, oldSelectedRoles]
	) => {
		if (props.allocatedTimes && oldSelectedUsers && oldSelectedRoles) {
			const addedUsers = newSelectedUsers.filter(
				(userId) => !oldSelectedUsers.includes(userId)
			);
			const addedRoles = newSelectedRoles.filter(
				(role) =>
					!oldSelectedRoles.some((oldRole) => oldRole.id === role.id)
			);

			if (addedUsers.length > 0 || addedRoles.length > 0) {
				const currentTimes = copyAllocatedTimes();

				addedUsers.forEach((userId) => {
					if (!currentTimes.users[userId]) {
						currentTimes.users[userId] = '0';
					}
				});

				addedRoles.forEach((role) => {
					if (!currentTimes.roles[role.id]) {
						currentTimes.roles[role.id] = '0';
					}
				});

				emit('update:allocated-times', currentTimes);
			}
		}
	},
	{ deep: true }
);

watch(searchQuery, (newQuery, oldQuery) => {
	if (!activeRoleFilterId.value) {
		return;
	}

	if (newQuery !== '') {
		const groupsWithMatches: UserAssignGroup['id'][] = [];

		userGroupListToDisplay.value.forEach((group) => {
			if (group.userList.length > 0) {
				groupsWithMatches.push(group.id);
			}
		});

		closedGroups.value = userGroupListToDisplay.value
			.filter((group) => !groupsWithMatches.includes(group.id))
			.map((group) => group.id);
	}

	if (oldQuery !== '' && newQuery === '') {
		updateGroupsForRoleFilter();
		scrollToGroup(listContentElement(), headerElement());
	}
});
</script>
